import { useEffect, useMemo, useState } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Button, Calendar, NavBar, Popup } from 'antd-mobile';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AiFillCloseCircle } from 'react-icons/ai';
import styled from 'styled-components';

import { useDate } from '~/libs/hooks/useDate';
import { useModalState } from '~/libs/hooks/useModalState';

export type RangePickerValue = [string | undefined, string | undefined];

export interface RangePickerProps {
  value?: RangePickerValue;
  startPlaceholder?: string;
  endPlaceholder?: string;
  onChange: (value: RangePickerValue) => void;
  placeholder?: string;
}

export const RangePicker = (props: RangePickerProps) => {
  const {
    value,
    startPlaceholder = 'YYYY-MM-DD',
    endPlaceholder = 'YYYY-MM-DD',
    onChange,
    placeholder,
  } = props;
  const { t } = useTranslation('global');
  const { formatDate } = useDate();

  const [visible, setVisible] = useModalState();
  const valueParser = (_value?: RangePickerValue): [Date, Date] | null => {
    if (!_value?.[0] && !_value?.[1]) return null;
    return [dayjs(_value?.[0]).toDate(), dayjs(_value?.[1]).toDate()];
  };

  const [_temp, _setTemp] = useState<[Date, Date] | null>(() => valueParser(value));

  useEffect(() => {
    _setTemp(() => valueParser(value));
  }, [visible, value]);

  const isActive = useMemo(() => {
    if (!value?.[0] && !value?.[1]) return false;
    return true;
  }, [value]);

  const displayDate = (date?: string, placeholder?: string) => {
    if (!date) return <span className="placeholder">{placeholder}</span>;
    return formatDate(date);
  };

  const handleConfirm = () => {
    if (!_temp) {
      onChange([undefined, undefined]);
    } else {
      onChange([dayjs(_temp?.[0]).format(), dayjs(_temp?.[1]).format()]);
    }

    setVisible(false);
  };

  return (
    <>
      <RangePickerButton className={isActive ? 'active' : ''} onClick={() => setVisible(true)}>
        <Box className={!value ? 'placeholder' : ''} flex={1}>
          {placeholder ? (
            value?.[0] ? (
              <>
                {displayDate(value[0])} <span className={value?.[1] ? '' : 'placeholder'}> ~ </span>
                {displayDate(value?.[1])}
              </>
            ) : (
              placeholder
            )
          ) : (
            <>
              {displayDate(value?.[0], startPlaceholder)}
              <span className={value?.[1] ? '' : 'placeholder'}> ~ </span>
              {displayDate(value?.[1], endPlaceholder)}
            </>
          )}
        </Box>

        {isActive ? (
          <CloseButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange([undefined, undefined]);
            }}
          >
            <AiFillCloseCircle />
          </CloseButton>
        ) : (
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23 9.40625H19.7188V8.15625C19.7188 8.07031 19.6484 8 19.5625 8H18.4688C18.3828 8 18.3125 8.07031 18.3125 8.15625V9.40625H13.3125V8.15625C13.3125 8.07031 13.2422 8 13.1562 8H12.0625C11.9766 8 11.9062 8.07031 11.9062 8.15625V9.40625H8.625C8.2793 9.40625 8 9.68555 8 10.0312V23C8 23.3457 8.2793 23.625 8.625 23.625H23C23.3457 23.625 23.625 23.3457 23.625 23V10.0312C23.625 9.68555 23.3457 9.40625 23 9.40625ZM22.2188 22.2188H9.40625V14.7969H22.2188V22.2188ZM9.40625 13.4688V10.8125H11.9062V11.75C11.9062 11.8359 11.9766 11.9062 12.0625 11.9062H13.1562C13.2422 11.9062 13.3125 11.8359 13.3125 11.75V10.8125H18.3125V11.75C18.3125 11.8359 18.3828 11.9062 18.4688 11.9062H19.5625C19.6484 11.9062 19.7188 11.8359 19.7188 11.75V10.8125H22.2188V13.4688H9.40625Z"
              fill="#404040"
            />
          </svg>
        )}
      </RangePickerButton>
      <Popup closeOnMaskClick destroyOnClose visible={visible} onClose={() => setVisible(false)}>
        <NavBar
          back={
            <Button size="small" onClick={() => setVisible(false)}>
              {t('global:m_button_close')}
            </Button>
          }
          backArrow={false}
          right={
            <Button color="primary" size="small" onClick={() => handleConfirm()}>
              {t('global:m_button_done')}
            </Button>
          }
        ></NavBar>
        <CalendarWrap>
          <Calendar
            allowClear
            className="calendar-custom"
            selectionMode="range"
            value={_temp}
            onChange={_setTemp}
          />
        </CalendarWrap>
      </Popup>
    </>
  );
};

const CloseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;

  color: #cdcdcd;
  font-size: 1rem;
  text-decoration: none;
  background: none;
  border: none;
  outline: none;
`;

const RangePickerButton = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding-left: 12px;
  padding-right: 6px;
  border-radius: 4px;
  border: 1px solid #eeeeee;

  color: #2c384a;
  font-size: 1rem;

  &.active {
    border-color: var(--color-primary-400);
  }

  .placeholder {
    color: #cccccc;
  }
`;

const CalendarWrap = styled.div`
  .calendar-custom {
    .adm-calendar-cell {
      justify-content: center;
      height: 38px;

      &-bottom {
        display: none;
      }

      &.adm-calendar-cell-selected.adm-calendar-cell.adm-calendar-cell-selected {
        background: rgba(229, 242, 250, 1);

        .adm-calendar-cell-top {
          height: 38px;
          width: 38px;
          border-radius: 99px;
          display: flex;
          justify-content: center;
          align-items: center;
          background: rgba(229, 242, 250, 1);
          color: var(--adm-color-primary);
        }

        &.adm-calendar-cell-selected-begin {
          background: linear-gradient(to right, var(--adm-color-white) 50%, #e5f2fa 51%, #e5f2fa);
          .adm-calendar-cell-top {
            height: 38px;
            width: 38px;
            border-radius: 99px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: var(--adm-color-primary);
            color: var(--adm-color-white);
          }

          &.adm-calendar-cell-selected-row-end {
            background: transparent;
          }
        }

        &.adm-calendar-cell-selected-begin.adm-calendar-cell-selected-end {
          background: var(--adm-color-white);
        }

        &.adm-calendar-cell-selected-end {
          background: linear-gradient(
            to right,
            #e5f2fa 50%,
            var(--adm-color-white) 51%,
            var(--adm-color-white)
          );
          .adm-calendar-cell-top {
            height: 38px;
            width: 38px;
            border-radius: 99px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: var(--adm-color-primary);
            color: var(--adm-color-white);
          }

          &.adm-calendar-cell-selected-row-start {
            background: transparent;
          }
        }

        &.adm-calendar-cell-selected-row-begin {
          border-top-left-radius: 99px;
          border-bottom-left-radius: 99px;
        }
        &.adm-calendar-cell-selected-row-end {
          border-top-right-radius: 99px;
          border-bottom-right-radius: 99px;
        }
      }
    }
  }
`;
